import React, { useEffect, useCallback, useContext, useState, useRef } from 'react';
import PatientDashboard from '../components/PatientDashboard';
import api from '../services/api';
import toast from 'react-hot-toast';
import useDashboardState from '../hooks/useDashboardState';
import { Search, Plus, FileText, Trash2, Save, ChevronDown, User, ChevronLeft, ChevronRight } from 'lucide-react';
import AuthContext from '../context/AuthContext';
import { formatDate } from '../utils/dateUtils';

const LIMIT = 10;

export default function DashboardPage() {
  const { user } = useContext(AuthContext);
  const {
    state,
    setPacientes,
    setPacienteSelecionado,
    setSessoesPaciente,
    setSessaoSelecionada,
    setEvolucao,
    setSearchTerm,
    setFilteredPacientes,
    setLoading,
    resetForm,
  } = useDashboardState();

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPacientes, setTotalPacientes] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceRef = useRef(null);

  const pacienteSelecionado = state.pacientes.find(p => p._id === state.pacienteSelecionado);

  const buscarPacientes = useCallback(async (search = '', pageNum = 1) => {
    setSearchLoading(true);
    try {
      const params = new URLSearchParams({ page: pageNum, limit: LIMIT });
      if (search.trim()) params.append('search', search.trim());
      const response = await api.get(`/patients?${params}`);
      const { data, pages, total } = response.data;
      setPacientes(data);
      setTotalPages(pages || 1);
      setTotalPacientes(total || data.length);
      setFilteredPacientes([]);
    } catch (err) {
      toast.error("Falha ao carregar a lista de pacientes.");
    } finally {
      setSearchLoading(false);
    }
  }, [setPacientes, setFilteredPacientes]);

  useEffect(() => {
    buscarPacientes('', 1);
  }, [buscarPacientes]);

  // Debounce na busca — espera 400ms após o último caractere
  useEffect(() => {
    if (state.pacienteSelecionado) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      buscarPacientes(state.searchTerm, 1);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [state.searchTerm, buscarPacientes, state.pacienteSelecionado]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    buscarPacientes(state.searchTerm, newPage);
  };

  const handlePatientSelectChange = async (patientId) => {
    setSearchTerm('');
    setFilteredPacientes([]);
    setPacienteSelecionado(patientId);
    resetForm();

    if (patientId) {
      setLoading(true);
      try {
        const response = await api.get(`/patients/${patientId}/sessions`);
        setSessoesPaciente(response.data.data);
      } catch (error) {
        toast.error("Falha ao buscar as sessões do paciente.");
      } finally {
        setLoading(false);
      }
    }
  };

  const salvarEvolucao = async () => {
    if (!state.sessaoSelecionada || !state.evolucao.trim()) {
      return toast.error('Selecione uma sessão e descreva a evolução.');
    }
    if (pacienteSelecionado?.status === 'aguardando_avaliacao') {
      return toast.error('Realize a avaliação inicial antes de registrar evoluções.');
    }

    const promise = api.post('/evolutions', {
      sessionId: state.sessaoSelecionada,
      description: state.evolucao
    });

    toast.promise(promise, {
      loading: 'Salvando evolução...',
      success: 'Evolução salva com sucesso!',
      error: 'Falha ao salvar a evolução.'
    });

    try {
      await promise;
      resetForm();
      handlePatientSelectChange(state.pacienteSelecionado);
    } catch (error) {}
  };

  const handleDeletePatient = async () => {
    if (!state.pacienteSelecionado) return;
    const patientToDelete = state.pacientes.find(p => p._id === state.pacienteSelecionado);
    if (window.confirm(`Deletar "${patientToDelete?.nome}" e todo o seu histórico?`)) {
      const promise = api.delete(`/patients/${state.pacienteSelecionado}`);
      toast.promise(promise, {
        loading: 'Deletando paciente...',
        success: 'Paciente deletado!',
        error: 'Falha ao deletar.'
      });
      try {
        await promise;
        setPacienteSelecionado('');
        setSessoesPaciente([]);
        buscarPacientes(state.searchTerm, page);
      } catch (error) {}
    }
  };

  const handleGenerateReport = async () => {
    if (!state.pacienteSelecionado) return;
    const toastId = toast.loading('Gerando relatório...');
    try {
      const response = await api.get(`/patients/${state.pacienteSelecionado}/report`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_${(pacienteSelecionado?.nome || 'paciente').replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Relatório gerado!', { id: toastId });
    } catch (error) {
      toast.error('Não foi possível gerar o relatório.', { id: toastId });
    }
  };

  const handleAddSessions = async () => {
    if (!state.pacienteSelecionado) return;
    const n = window.prompt("Quantas novas sessões adicionar?", "5");
    if (!n || isNaN(parseInt(n)) || parseInt(n) <= 0) return toast.error("Número inválido.");
    const promise = api.post(`/patients/${state.pacienteSelecionado}/add-sessions`, { numberOfNewSessions: parseInt(n, 10) });
    toast.promise(promise, { loading: 'Adicionando...', success: 'Sessões adicionadas!', error: 'Falha.' });
    try { await promise; handlePatientSelectChange(state.pacienteSelecionado); } catch (error) {}
  };

  const handlePatientUpdate = (updatedPatient) => {
    setPacientes(state.pacientes.map(p => p._id === updatedPatient._id ? updatedPatient : p));
  };

  const showList = !state.pacienteSelecionado && state.searchTerm.trim() !== '' && state.pacientes.length > 0;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <p className="text-gray-500 text-sm">Olá, {user?.nome} 👋</p>
        <h1 className="text-3xl font-bold text-gray-900">Painel do Paciente</h1>
        <p className="text-gray-500 mt-1">Acompanhe a evolução e histórico do seu paciente</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Selecionar Paciente</h2>

            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Buscar paciente por nome ou diagnóstico..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                  value={state.searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={() => {
                    if (state.pacienteSelecionado) {
                      setPacienteSelecionado('');
                      setSessoesPaciente([]);
                      buscarPacientes('', 1);
                    }
                  }}
                />
                {searchLoading && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                )}
              </div>

              {state.pacienteSelecionado && (
                <div className="flex items-center gap-2">
                  <button onClick={handleAddSessions} className="bg-blue-600 hover:bg-blue-700 text-white p-2.5 rounded-xl transition-colors" title="Adicionar Sessões"><Plus className="w-4 h-4" /></button>
                  <button onClick={handleGenerateReport} className="bg-green-500 hover:bg-green-600 text-white p-2.5 rounded-xl transition-colors" title="Gerar Relatório"><FileText className="w-4 h-4" /></button>
                  <button onClick={handleDeletePatient} className="bg-red-500 hover:bg-red-600 text-white p-2.5 rounded-xl transition-colors" title="Deletar Paciente"><Trash2 className="w-4 h-4" /></button>
                </div>
              )}
            </div>

            {/* Lista de pacientes paginada */}
            {showList && (
              <div className="mt-3">
                <ul className="divide-y divide-gray-50 border border-gray-100 rounded-xl overflow-hidden">
                  {state.pacientes.map(p => (
                    <li
                      key={p._id}
                      className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3 text-sm transition-colors"
                      onClick={() => handlePatientSelectChange(p._id)}
                    >
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{p.nome}</p>
                        {p.diagnosisTitle && <p className="text-xs text-gray-400 truncate">{p.diagnosisTitle}</p>}
                      </div>
                    </li>
                  ))}
                </ul>

                {/* Paginação */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-3">
                    <p className="text-xs text-gray-400">{totalPacientes} paciente{totalPacientes !== 1 ? 's' : ''}</p>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 1}
                        className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeft className="w-4 h-4 text-gray-500" />
                      </button>
                      <span className="text-xs text-gray-600 px-2">{page} / {totalPages}</span>
                      <button
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page === totalPages}
                        className="w-7 h-7 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Paciente selecionado */}
            {state.pacienteSelecionado && pacienteSelecionado && (
              <div className="mt-4 flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{pacienteSelecionado.nome}</p>
                  <p className="text-xs text-gray-500">{formatDate(pacienteSelecionado.dateOfBirth)}</p>
                </div>
                <div className="ml-auto flex items-center gap-1.5">
                  {pacienteSelecionado.clinicaOrigemId && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">Clínica</span>
                  )}
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">Ativo</span>
                </div>
              </div>
            )}

            {/* Sem resultados */}
            {!state.pacienteSelecionado && !searchLoading && state.pacientes.length === 0 && (
              <p className="text-sm text-gray-400 text-center mt-4">
                {state.searchTerm ? 'Nenhum paciente encontrado.' : 'Nenhum paciente cadastrado.'}
              </p>
            )}
          </div>

          {state.pacienteSelecionado && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Registrar Nova Evolução</h2>
              <div className="space-y-3">
                <div className="relative">
                  <select
                    value={state.sessaoSelecionada}
                    onChange={(e) => setSessaoSelecionada(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl p-3 pr-10 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none appearance-none bg-white"
                  >
                    <option value="">Selecione a sessão...</option>
                    {state.sessoesDoPaciente
                      .filter(s => s.evolutionDescription === "Nenhuma evolução registrada.")
                      .map(s => (
                        <option key={s._id} value={s._id}>
                          Sessão {s.sessionNumber} • {s.sessionDate ? formatDate(s.sessionDate) : 'Sem data'}
                        </option>
                      ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                </div>

                {pacienteSelecionado?.status === 'aguardando_avaliacao' ? (
                  <div className="border border-yellow-200 bg-yellow-50 rounded-xl p-4 text-center space-y-2">
                    <p className="text-sm text-yellow-800 font-medium">
                      Este paciente ainda não possui avaliação inicial.
                    </p>
                    <button
                      onClick={() => window.location.href = `/avaliacao?patientId=${pacienteSelecionado._id}`}
                      className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium rounded-xl transition-colors"
                    >
                      Realizar Avaliação
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <textarea
                        placeholder="Anotar evolução do paciente..."
                        value={state.evolucao}
                        onChange={(e) => setEvolucao(e.target.value)}
                        className="w-full border border-gray-200 rounded-xl p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
                        rows={4}
                        disabled={!state.sessaoSelecionada}
                      />
                      <span className="absolute bottom-2 right-3 text-xs text-gray-400">{state.evolucao.length}/1000</span>
                    </div>
                    <button
                      onClick={salvarEvolucao}
                      className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white py-3 rounded-xl font-medium text-sm transition-colors flex items-center justify-center gap-2"
                      disabled={!state.sessaoSelecionada}
                    >
                      <Save className="w-4 h-4" />
                      Salvar Evolução
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 min-h-[400px]">
          {state.loading ? (
            <div className="flex justify-center items-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <PatientDashboard
              patient={pacienteSelecionado}
              sessions={state.sessoesDoPaciente}
              onPatientUpdate={handlePatientUpdate}
            />
          )}
        </div>
      </div>
    </div>
  );
}